import classNames from 'classnames';
import * as React from 'react';

export interface CodeInputProps {
    value: string;
    onChange: (value: string) => void;
    ariaLabel: string;
    length?: number;
    idPrefix?: string;
    disabled?: boolean;
    invalid?: boolean;
    className?: string;
}

export const CodeInput: React.FunctionComponent<CodeInputProps> = ({value, onChange, ariaLabel, length = 6, idPrefix = 'code-digit', disabled, invalid, className}: CodeInputProps) => {
    const cellsRef = React.useRef<(HTMLInputElement | null)[]>([]);
    const digits = Array.from({length}, (_, i) => value[i] ?? '');
    const onCellChange = (index: number, raw: string) => {
        const digit = raw.replace(/\D/g, '').slice(0, 1);
        const next = digits.slice();
        next[index] = digit;
        onChange(next.join(''));
        if (digit && index < length - 1) {
            cellsRef.current[index + 1]?.focus();
        }
    };
    const onCellKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            cellsRef.current[index - 1]?.focus();
        }
    };
    const onCellPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
        if (!pasted) {
            return;
        }
        e.preventDefault();
        onChange(pasted.slice(0, length));
        cellsRef.current[Math.min(pasted.length, length - 1)]?.focus();
    };
    return (
        <div role="group" aria-label={ariaLabel} className={classNames('mp-code-input', className)}>
            {digits.map((digit, i) => (
                <input
                    key={i}
                    ref={el => {
                        cellsRef.current[i] = el;
                    }}
                    id={`${idPrefix}-${i + 1}`}
                    className={classNames('mp-code-input__cell', {
                        'mp-code-input__cell--filled': !!digit,
                        'mp-code-input__cell--invalid': invalid,
                    })}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    aria-label={`Digit ${i + 1}`}
                    value={digit}
                    disabled={disabled}
                    onChange={e => onCellChange(i, e.target.value)}
                    onKeyDown={e => onCellKeyDown(i, e)}
                    onPaste={onCellPaste}
                />
            ))}
        </div>
    );
};
